import React from 'react';
import {
	BasicButton,
	Checkbox,
	Scrollbar,
} from '../../UI';
import { getPrimary, getSecondary } from '../../styles/colors';
import { TranslateProvider } from '../../containers/TranslationsProvider';
import ReorderPointsModal from './ReorderPointsModal';
import FormRootLayout from './FormRootLayout';
import MenuItemTabs from '../../UI/MenuItemTabs';
import { NO_FORM_SECTIONS } from '../../utils/constants';
import { getText } from '../../template/utils/functions';
import ModalNewItem from './ModalNewItem';
import ModalNewInput from './ModalNewInput';
import ModalEditInput from './ModalEditInput';
import ModalTranslate from './ModalTranslate';


const InputsInPortal = ({ portal, updateState }) => {
	const [inputs, setInputs] = React.useState(new Map(Object.entries(portal.config.inputs)));
	const [selecteOptionMenu, setSelecteOptionMenu] = React.useState(
		Object.values(portal?.config?.sections || {})
			.filter(sec => !NO_FORM_SECTIONS.includes(sec.value) && sec.isActive)
			?.[0].value
	);
	const translate = React.useContext(TranslateProvider);

	const getInputs = React.useCallback(() => {
		setInputs(new Map(Object.entries(portal?.config?.inputs || {})));
	}, [portal.config.inputs]);

	React.useEffect(() => {
		getInputs();
	}, [portal.config.inputs, selecteOptionMenu]);

	const renderForm = React.useCallback(() => {
		const { sections } = portal.config;
		const tabs = [];

		tabs.push(
			...Object.values(sections || {})
				.filter(sec => !NO_FORM_SECTIONS.includes(sec.value) && sec.isActive)
		);

		return (
			<div style={{ height: '100%' }}>
				{/* NAVBAR */}
				<MenuItemTabs
					items={tabs}
					translate={translate}
					setSelected={setSelecteOptionMenu}
					selected={selecteOptionMenu}
					colorSelected={'#000000'}
				/>
				{/* INPUTS */}
				<div style={{ height: '100%' }}>
					<div style={{ height: '100%' }}>
						<CardsCheckbox
							inputs={inputs}
							section={selecteOptionMenu}
							updateInputs={setInputs}
							portal={portal}
							translate={translate}
						/>
					</div>
				</div>
			</div>
		);
	}, [selecteOptionMenu, inputs]);

	return (
		<FormRootLayout
			noScrollLayout={true}
			form={renderForm()}
			button={
				<BasicButton
					color='white'
					text={translate.save}
					onClick={() => {
						updateState({
							inputs: {
								access: inputs.get('access'),
								representation: inputs.get('representation'),
								delegation: inputs.get('delegation'),
								earlyVote: inputs.get('earlyVote'),
								interventions: inputs.get('interventions')
							},
						});
					}}
					backgroundColor={{
						backgroundColor: getSecondary(),
						border: `1px solid ${getSecondary()}`,
						boxShadow: 'none',
						color: 'white',
						fontWeight: 'bold'
					}}
				/>
			}
		/>
	);
};

export default InputsInPortal;


const CardsCheckbox = ({
	inputs,
	section,
	updateInputs,
	portal,
	translate,
}) => {
	const [personActive, setPersonActive] = React.useState(Object.keys(...inputs.values())?.[0]);
	const [personOptions, setPersonOptions] = React.useState(Object.keys(...inputs.values()) || []); // person, entity...
	const [localState, setLocalState] = React.useState(inputs.get(section)?.person || []);
	const generationKey = React.useMemo(() => Math.random().toString(36).substr(2, 9), [inputs[section]]);

	const editCheckboxInputs = (data, index) => {
		setLocalState([
			...localState.slice(0, index++),
			data,
			...localState.slice(index++, localState.length)
		]);
	};

	const removeCheckboxInputs = index => {
		const remove = localState.filter((val, number) => (number !== index));
		setLocalState([...remove]);
	};

	const getFilteredPersonOptions = React.useCallback(() => Object.keys(...inputs.values())
		.filter(
			person => person === 'person'
				// Entity and personOrEntity is active
				|| (
					person === 'entity'
					&& inputs.get(section).person.find(inp => inp.name === 'personOrEntity')?.isActive
				)
				// Representative and representation or optional representation
				|| (
					person === 'representative'
					&& (
						section === 'representation'
						|| portal.config?.optionalFields[section].representation === true
						|| (section === 'access' && inputs.get(section).person.find(inp => inp.name === 'personOrEntity')?.isActive)
					)
				)
				// Delegate and delegation or optional delegation
				|| (
					person === 'delegate'
					&& (
						section === 'delegation'
						|| portal.config?.optionalFields[section].delegation === true
					)
				)
		), [inputs]);

	React.useEffect(() => {
		updateInputs(inputs.set(section, {
			...inputs.get(section),
			[personActive]: [...localState]
		}));
		setPersonOptions(getFilteredPersonOptions() || []);
	}, [inputs, localState]);

	React.useEffect(() => {
		setLocalState(inputs.get(section)[personActive] || []);
	}, [personActive, section]);

	const isDuplicatedName = name => {
		if (localState.find(input => input.name === name)) {
			return true;
		}
		return false;
	};

	return (
		<div style={{ height: '100%' }}>
			<div style={{ display: 'flex', alignItems: 'center', columnGap: '2rem' }}>
				{personOptions
				&& personOptions.map(person => <BasicButton
					key={person}
					backgroundColor={{
						background: personActive === person ? getPrimary() : undefined,
						color: personActive === person ? 'white' : 'black',
					}}
					onClick={() => setPersonActive(person)}
					text={`Editar ${translate?.[person]}`}
				/>)}

				<ModalNewInput
					translate={translate}
					input={{
						label: '',
						name: `labelCustom_${Math.random().toString(10).substr(2, 2)}`,
						text: {
							es: '',
							en: '',
							gal: '',
							cat: '',
							eu: '',
							fr: '',
							pt: '',
							pl: ''
						},
						type: 'text',
						valueType: 'text',
						isActive: true,
						isCustom: true,
						required: false,
						confirm: false,
						checkbbdd: false
					}}
					label={'Nuevo input'}
					isDuplicatedName={isDuplicatedName}
					setLocalState={newData => {
						setLocalState([
							...localState,
							newData
						]);
					}}
				/>
				<ReorderPointsModal label={'Reorganizar'} translate={translate} items={localState} updateAndSave={setLocalState} />
			</div>
			<div style={{ marginTop: '1rem', height: 'calc( 100% - 8em )' }}>
				<Scrollbar>
					<div style={{ paddingRight: '1rem' }}>
						{
							localState.map((val, index) => (
								<CheckBoxInputs
									key={`checkboxinputs_${generationKey}${index}`}
									input={val}
									index={index}
									translate={translate}
									isDuplicatedName={isDuplicatedName}
									editCheckboxInputs={editCheckboxInputs}
									removeCheckboxInputs={removeCheckboxInputs}
								/>
							))
						}
					</div>
				</Scrollbar>
			</div>
		</div>
	);
};

const CheckBoxInputs = ({
	input,
	index,
	editCheckboxInputs,
	removeCheckboxInputs,
	isDuplicatedName,
	translate
}) => {
	const {
		label,
		items,
		name,
		text,
		isActive,
		isCustom,
		required,
		confirm,
		type,
		// checkbbdd
	} = input;
	const [openItems, setOpenItems] = React.useState(false);

	const editItem = (data, indexItem) => {
		editCheckboxInputs({
			...input,
			items: [
				...input.items.slice(0, indexItem++),
				data,
				...input.items.slice(indexItem++, input.items.length)
			]
		}, index);
	};

	const removeItem = indexItem => {
		const remove = input.items.filter((val, number) => (number !== indexItem));
		editCheckboxInputs({
			...input,
			items: [...remove]
		}, index);
	};

	const isDuplicatedItem = value => {
		if (items.find(item => item.value === value)) {
			return true;
		}
		return false;
	};

	const getNewInput = () => ({
		value: '',
		valueType: input?.valueType || 'text',
		label: `newItem_${input.items.length}`,
		text: {
			es: '',
			en: '',
			gal: '',
			cat: '',
			eu: '',
			fr: '',
			pt: '',
			pl: ''
		},
	});


	return (
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'start',
				justifyContent: 'space-between',
				marginBottom: '.8rem',
				border: `2px solid ${getPrimary()}`,
				borderRadius: '8px',
				padding: '.2rem',
				boxShadow: '5px 2px 5px rgba(0, 0, 0, 0.2)'
			}}
		>
			{/* INPUT */}
			<div
				style={{
					display: 'flex',
					width: '100%',
					flexDirection: 'row',
					alignItems: 'center',
					justifyContent: 'space-between',
					marginBottom: '.8rem',
					padding: '.2rem',
				}}
			>
				<div style={{
					display: 'flex',
					flexDirection: 'row',
					alignItems: 'center',
					justifyContent: 'space-between',
				}}>
					<Checkbox
						label={(text[translate.currentLanguage] && `${text[translate.currentLanguage]} (${name})`)
							|| `${translate[label]} (${name})`}
						value={isActive}
						onChange={(ev, isChecked) => {
							const data = {
								...input,
								isActive: isChecked
							};
							editCheckboxInputs(data, index);
						}}
					/>
					<span style={{ marginLeft: '1rem', fontStyle: 'italic' }}
					>
						{input?.isCustom ? '{CUSTOM}' : '{DEFAULT}'}
					</span>
					<span style={{ marginLeft: '1rem' }} >
						[Tipo: {type}]
					</span>
					<span
						style={{
							marginLeft: '1rem',
							cursor: items ? 'pointer' : 'default',
							fontWeight: 'bold'
						}}
						onClick={() => setOpenItems(!openItems)}
					>
						{items && (openItems ? 'COLAPSAR' : 'DESPLEGAR')}
					</span>
				</div>
				<div style={{ display: 'flex', alignItems: 'center' }}>
					{items && openItems && input?.isCustom
					&& <ModalNewItem
						translate={translate}
						item={getNewInput()}
						label={'Añadir opción'}
						isDuplicatedItem={isDuplicatedItem}
						setLocalState={newInput => {
							const data = {
								...input,
								items: [
									...input.items,
									newInput
								]
							};
							editCheckboxInputs(data, index);
						}}
					/>}

					{input?.isCustom
					&& <ModalEditInput
						translate={translate}
						input={input}
						label={'Editar input'}
						isDuplicatedName={isDuplicatedName}
						setLocalState={newData => {
							const data = {
								...input,
								...newData
							};
							editCheckboxInputs(data, index);
						}}
					/>}

					<ModalTranslate
						translate={translate}
						item={input}
						name={input.name}
						setLocalState={newData => {
							const data = {
								...input,
								...newData
							};
							editCheckboxInputs(data, index);
						}}
					/>

					<button
						style={{
							border: `1px solid ${getSecondary()}`,
							borderRadius: '4px',
							padding: '.5rem',
							background: confirm ? getPrimary() : 'transparent',
							cursor: 'pointer',
							marginRight: '.4rem',
							color: confirm ? 'white' : getSecondary(),
							marginLeft: '1em'
						}}
						onClick={() => {
							const data = {
								...input,
								confirm: !confirm
							};
							editCheckboxInputs(data, index);
						}}
					>
						Confirmar
					</button>

					<button
						style={{
							border: `1px solid ${getSecondary()}`,
							borderRadius: '4px',
							padding: '.5rem',
							background: required ? getPrimary() : 'transparent',
							cursor: 'pointer',
							marginRight: '.4rem',
							color: required ? 'white' : getSecondary(),
						}}
						onClick={() => {
							const data = {
								...input,
								required: !required
							};
							editCheckboxInputs(data, index);
						}}
					>
						Requerido
					</button>

					{isCustom
					&& <button
						style={{
							border: `1px solid ${getSecondary()}`,
							borderRadius: '4px',
							padding: '.5rem',
							background: 'transparent',
							cursor: 'pointer',
							color: getSecondary(),
						}}
						onClick={() => {
							removeCheckboxInputs(index);
						}}
					>
						Eliminar
					</button> }
				</div>
			</div>
			{/* ITEMS */}
			{items && openItems
			&& <div
				style={{
					display: 'flex',
					width: '100%',
					flexDirection: 'column',
					alignItems: 'start',
					borderTop: `2px solid ${getPrimary()}`,
					justifyContent: 'space-between',
					padding: '.2rem',
					paddingLeft: '2rem',
					marginBottom: '.8rem',
				}}
			>
				{items.map((item, itemIndex) => (
					<Items
						key={item.value}
						item={item}
						isCustom={input?.isCustom}
						editItem={data => editItem(data, itemIndex)}
						isDuplicatedItem={isDuplicatedItem}
						removeItem={() => removeItem(itemIndex)}
						translate={translate}
					/>
				))}
			</div>}
		</div>
	);
};

const Items = ({
	item,
	editItem,
	isDuplicatedItem,
	removeItem,
	isCustom,
	translate
}) => (
	<div
		style={{
			display: 'flex',
			width: '100%',
			flexDirection: 'row',
			alignItems: 'center',
			justifyContent: 'space-between',
			marginBottom: '.8rem',
			padding: '.2rem',
		}}
	>
		<div
			style={{
				display: 'flex',
				width: '100%',
				justifyContent: 'start',
				gap: '2rem',
				alignItems: 'center',
				fontSize: '1rem'
			}}
		>
			{/* INPUT DATA */}
			<div >{getText(item, translate)}</div>
			<div >Valor: {item.value}</div>
			<div >Tipo de valor: {item?.valueType}</div>
		</div>
		<div
			style={{
				display: 'flex',
				flexDirection: 'row',
				alignItems: 'center',
				columnGap: '2rem',
			}}
		>
			{!isCustom
			&& <ModalTranslate
				translate={translate}
				item={item}
				name={item.value}
				setLocalState={newData => {
					const data = {
						...item,
						...newData
					};
					editItem(data);
				}}
			/>}
			{isCustom
			&& <ModalNewItem
				translate={translate}
				item={item}
				label={'Editar opción'}
				isDuplicatedItem={isDuplicatedItem}
				setLocalState={editItem}
			/>}
			{/* Remove */}
			{isCustom
			&& <button
				style={{
					border: `1px solid ${getSecondary()}`,
					borderRadius: '4px',
					padding: '.5rem',
					background: 'transparent',
					cursor: 'pointer',
					color: getSecondary(),
				}}
				onClick={() => removeItem()}
			>
				Eliminar
			</button>}
		</div>
	</div>
);

/* <button
	style={{
		border: `1px solid ${getSecondary()}`,
		borderRadius: '4px',
		padding: '.5rem',
		background: checkbbdd ? getPrimary() : 'transparent',
		cursor: 'pointer',
		marginRight: '.4rem',
		color: checkbbdd ? 'white' : getSecondary(),
		marginLeft: '1em'
	}}
	onClick={() => {
		const data = {
			...input,
			checkbbdd: !checkbbdd
		};
		editCheckboxInputs(data, index);
	}}
>
	Comprobar contra BBDD
</button> */
